<script setup>
// The member's own waiting state on a competition page (JOIN_SETTINGS_PLAN.md §7.7):
// "You're in — waiting to be placed", the partner picker for doubles, and Withdraw.
import PreferredPartnersDialog from '~/components/placement/PreferredPartnersDialog.vue';
import {usePendingPlacements} from '~/composables/usePendingPlacements';
import {usePaymentsDisplay} from '~/composables/usePaymentsDisplay';
import {heldJoinFeeLine} from '~/composables/usePayingHonestly';

const props = defineProps({
  // CurrentUserPendingPlacementResponse from the entity detail
  placement: {type: Object, required: true},
  entityName: {type: String, default: ''},
});
const emit = defineEmits(['changed']);

const {dismiss, actionPending, actionErrors} = usePendingPlacements();
const {setAlertMessage} = useAlertService();
const partnersOpen = ref(false);

const withdraw = async () => {
  if (await dismiss(props.placement.id)) {
    setAlertMessage(`You've left the waiting list for ${props.entityName || 'this competition'}`);
    emit('changed');
  } else {
    setAlertMessage({
      message: actionErrors.value?.global?.[0] || "Couldn't leave the waiting list. Please try again",
      color: 'error',
    });
  }
};

// The fee is owed once placed (R2 batch B): the API sends the competition's fee.
const {money} = usePaymentsDisplay();
const feeLine = computed(() => props.placement.entryFeeMinor != null
    ? heldJoinFeeLine(money(props.placement.entryFeeMinor, props.placement.entryFeeCurrency))
    : null);

const partners = computed(() => props.placement.preferredPartners ?? []);
const pickedBy = computed(() =>
    (props.placement.preferredBy ?? []).filter(b => !partners.value.some(p => p.playerId === b.playerId)));
</script>

<template>
  <div class="tl-card apb">
    <div class="apb-main">
      <div class="apb-ic"><v-icon color="success">mdi-check-decagram</v-icon></div>
      <div class="flex-fill min-w-0">
        <div class="apb-title">You're in, waiting to be placed</div>
        <div class="apb-sub">{{ placement.placementDescription || 'An admin will place you.' }} We'll let you know.<template v-if="feeLine"> {{ feeLine }}</template></div>
      </div>
      <crud-confirm-dialog
          title="Leave the waiting list?"
          :message="`You'll no longer be waiting to join ${entityName || 'this competition'}. You can join again later.`"
          confirm-text="Leave"
          cancel-text="Keep waiting"
          @confirm="withdraw">
        <template #default="{click}">
          <v-btn variant="outlined" color="error" size="small" rounded="pill" :loading="actionPending" @click="click">Withdraw</v-btn>
        </template>
      </crud-confirm-dialog>
    </div>

    <div v-if="placement.canPickPartners" class="apb-partners">
      <div class="apb-partners-text">
        <template v-if="partners.length">
          <span class="apb-label">Preferred partner{{ partners.length > 1 ? 's' : '' }}:</span>
          <span v-for="p in partners" :key="p.playerId" class="tl-chip apb-chip"
                :class="p.isMutual ? 'tl-chip--ok' : (p.stillWaiting ? 'tl-chip--brand' : '')">
            <v-icon v-if="p.isMutual" size="12" icon="mdi-heart" class="mr-1"/>{{ p.fullName }}{{ p.stillWaiting ? '' : ' (placed)' }}
          </span>
        </template>
        <template v-else>
          <span class="apb-label">Anyone you'd like to team up with?</span>
        </template>
        <span v-for="b in pickedBy" :key="'b' + b.playerId" class="tl-chip tl-chip--warn apb-chip">
          {{ b.fullName }} picked you
        </span>
      </div>
      <v-btn variant="text" color="primary" size="small" rounded="pill" :prepend-icon="partners.length ? 'mdi-pencil' : 'mdi-account-heart-outline'"
             @click="partnersOpen = true">
        {{ partners.length ? 'Change' : 'Pick a partner' }}
      </v-btn>
    </div>

    <preferred-partners-dialog v-model="partnersOpen" :placement="placement" :entity-name="entityName" @saved="emit('changed')"/>
  </div>
</template>

<style scoped lang="scss">
.apb {
  margin-bottom: 20px;
  overflow: hidden;
}

.apb-main {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
}

.apb-ic {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgb(var(--v-theme-successTint));
}

.apb-title {
  font-size: 14px;
  font-weight: 700;
}

.apb-sub {
  font-size: 12px;
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 2px;
  line-height: 1.45;
}

.apb-partners {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  border-top: 1px solid rgb(var(--v-theme-borderLight));
  background: rgb(var(--v-theme-light));
}

.apb-partners-text {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
}

.apb-label {
  font-weight: 600;
  color: rgb(var(--v-theme-charcoal));
}

.apb-chip {
  display: inline-flex;
  align-items: center;
  font-size: 10.5px;
  padding: 2px 8px;
}
</style>
